import React, { useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/system"
import { DateTime } from "luxon"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()({
    input: {
        width: "22rem",
    },
})

export type WeekOption = {
    label: string
    start: DateTime
    end: DateTime
    weekNumber: number
}

type SelectWeekAutocompleteProps = Readonly<{
    weeks: WeekOption[]
    startDate: DateTime
    endDate: DateTime
    setStartDate: React.Dispatch<React.SetStateAction<DateTime>>
    setEndDate: React.Dispatch<React.SetStateAction<DateTime>>
}>

export function SelectWeekAutocomplete({
    weeks,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
}: SelectWeekAutocompleteProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [selectedWeek, setSelectedWeek] = useState<string | null>(null)

    useEffect(() => {
        const startNormalized = startDate.startOf("day")
        const endNormalized = endDate.endOf("day")
        const currentWeek = weeks.find(
            (week) => startNormalized >= week.start.startOf("day") && endNormalized <= week.end.endOf("day"),
        )
        if (currentWeek) {
            setSelectedWeek(currentWeek.label)
        }
    }, [weeks])

    const handleChange = (newValue: WeekOption | null) => {
        if (newValue) {
            setSelectedWeek(newValue.label)
            setStartDate(newValue.start)
            setEndDate(newValue.end)
        }
    }

    return (
        <LocalizedStrict id="merchant-menu-planning-table-week-select" attrs={{ label: true }}>
            <ApiQueryAutocomplete
                label="Select Week"
                options={weeks || []}
                getOptionLabel={(option: WeekOption) =>
                    l10n.getString("merchant-menu-planning-table-week-select-option", {
                        countOfWeek: option.weekNumber,
                        startDate: option.start.toFormat("dd/LL/yyyy"),
                        endDate: option.end.toFormat("dd/LL/yyyy"),
                    })
                }
                value={weeks.find((week) => week.label === selectedWeek) || null}
                onChange={(event: React.SyntheticEvent, newValue: WeekOption | null) => handleChange(newValue)}
                renderOption={(props, option: WeekOption) => (
                    <LocalizedStrict
                        id="merchant-menu-planning-table-week-select-option"
                        vars={{
                            countOfWeek: option.weekNumber,
                            startDate: option.start.toFormat("dd/LL/yyyy"),
                            endDate: option.end.toFormat("dd/LL/yyyy"),
                        }}
                    >
                        <Box component="li" sx={{ ml: "0.5rem" }} {...props}>
                            {option.label}
                        </Box>
                    </LocalizedStrict>
                )}
                className={classes.input}
                isLoading={!weeks.length}
                isError={false}
            />
        </LocalizedStrict>
    )
}
